import { useEffect, useRef, useState } from "react";
import usePlacesAutocomplete from "use-places-autocomplete";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import { resolvePlace } from "../utils/placeAddress";

/**
 * Address search for the venue forms.
 * On pick: setSelected({ lat, lng }, formattedAddress, addressComponents).
 * Coordinates come from Places details for the suggestion's place_id
 * (geocoding is a fallback). If the lookup fails, an error is shown and
 * setSelected is not called.
 */
const PlacesAutocomplete = ({ setSelected, defaultAddress = "" }) => {
  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete();
  const [error, setError] = useState("");
  const [looking, setLooking] = useState(false);
  // description → place_id for the current suggestions (Combobox only gives us the text)
  const placeIds = useRef({});

  useEffect(() => {
    if (defaultAddress) {
      setValue(defaultAddress, false);
    }
  }, [defaultAddress, setValue]);

  useEffect(() => {
    if (status === "OK") {
      data.forEach(({ description, place_id }) => {
        placeIds.current[description] = place_id;
      });
    }
  }, [status, data]);

  const handleSelect = async (description) => {
    setValue(description, false);
    clearSuggestions();
    setError("");
    setLooking(true);
    try {
      const place = await resolvePlace({ placeId: placeIds.current[description], description });
      setSelected({ lat: place.lat, lng: place.lng }, place.address, place.components);
    } catch (e) {
      console.error("[PlacesAutocomplete]", e);
      setError("We couldn't find that address on the map. Please try again, or pick another suggestion.");
    } finally {
      setLooking(false);
    }
  };

  return (
    <>
      <Combobox onSelect={handleSelect} aria-label="Business address">
        <ComboboxInput
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError("");
          }}
          disabled={!ready}
          className="combobox-input"
          placeholder="Search an address"
          aria-describedby={error ? "places-autocomplete-error" : undefined}
          aria-invalid={error ? "true" : undefined}
        />
        <ComboboxPopover>
          <ComboboxList>
            {status === "OK" &&
              data.map(({ place_id, description }) => (
                <ComboboxOption key={place_id} value={description} />
              ))}
          </ComboboxList>
        </ComboboxPopover>
      </Combobox>
      <div aria-live="polite">
        {looking ? <p className="places-autocomplete-status">Finding that address…</p> : null}
        {error ? (
          <p id="places-autocomplete-error" role="alert" style={{ color: "#b91c1c" }}>
            {error}
          </p>
        ) : null}
      </div>
    </>
  );
};

export default PlacesAutocomplete;
